// Trigonometry Knowledge Graph Samples - Part 1
// Organized by difficulty levels with systematic naming

export const trigonometrySamplesPart1 = {
  fundamental: [
    {
      id: 'trig_fund_01',
      problem: "Find sin(30°)",
      problemType: "trigonometry",
      difficulty: "fundamental",
      description: "Basic sine function with common angle",
      graphData: {
        nodes: [
          {
            id: "trigonometry",
            name: "Trigonometry",
            type: "concepts",
            description: "Study of relationships between angles and sides of triangles",
            value: "Angle and side relationships",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "sine_function",
            name: "Sine Function",
            type: "concepts",
            description: "Ratio of opposite side to hypotenuse",
            value: "sin(θ) = opposite/hypotenuse",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "special_angles",
            name: "Special Angles",
            type: "concepts",
            description: "Common angles with known trigonometric values",
            value: "30°, 45°, 60°, 90°",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "sine_formula",
            name: "Sine Formula",
            type: "formulas",
            description: "sin(θ) = opposite/hypotenuse",
            value: "sin(θ) = opposite/hypotenuse",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "angle_30",
            name: "Angle 30°",
            type: "values",
            description: "The given angle",
            value: "30°",
            importance: 10,
            x: 0, y: 60, z: 35
          },
          {
            id: "sine_30_result",
            name: "Sine 30° Result",
            type: "values",
            description: "The sine of 30 degrees",
            value: "sin(30°) = 1/2 = 0.5",
            importance: 9,
            x: 0, y: 150, z: -15
          },
          {
            id: "identify_angle",
            name: "Identify Angle",
            type: "steps",
            description: "Extract the angle from the problem",
            value: "Step 1: Angle = 30°",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "recall_special_value",
            name: "Recall Special Value",
            type: "steps",
            description: "Remember the exact value for 30°",
            value: "Step 2: sin(30°) = 1/2",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "convert_to_decimal",
            name: "Convert to Decimal",
            type: "steps",
            description: "Convert fraction to decimal if needed",
            value: "Step 3: 1/2 = 0.5",
            importance: 8,
            x: 0, y: 50, z: 40
          },
          {
            id: "verify_result",
            name: "Verify Result",
            type: "steps",
            description: "Check using unit circle or calculator",
            value: "Step 4: Result verified",
            importance: 7,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "trigonometry", to: "sine_function", type: "includes", strength: 9 },
          { from: "trigonometry", to: "special_angles", type: "includes", strength: 8 },
          { from: "angle_30", to: "sine_30_result", type: "used to calculate", strength: 9 },
          { from: "special_angles", to: "sine_30_result", type: "provides value for", strength: 8 },
          { from: "trigonometry", to: "identify_angle", type: "requires", strength: 8 },
          { from: "identify_angle", to: "recall_special_value", type: "enables", strength: 9 },
          { from: "recall_special_value", to: "convert_to_decimal", type: "leads to", strength: 8 },
          { from: "convert_to_decimal", to: "verify_result", type: "enables", strength: 7 },
          { from: "recall_special_value", to: "sine_30_result", type: "gives", strength: 9 }
        ]
      }
    },
    {
      id: 'trig_fund_02',
      problem: "Find cos(60°)",
      problemType: "trigonometry",
      difficulty: "fundamental",
      description: "Basic cosine function with common angle",
      graphData: {
        nodes: [
          {
            id: "trigonometry",
            name: "Trigonometry",
            type: "concepts",
            description: "Study of relationships between angles and sides of triangles",
            value: "Angle and side relationships",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "cosine_function",
            name: "Cosine Function",
            type: "concepts",
            description: "Ratio of adjacent side to hypotenuse",
            value: "cos(θ) = adjacent/hypotenuse",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "special_angles",
            name: "Special Angles",
            type: "concepts",
            description: "Common angles with known trigonometric values",
            value: "30°, 45°, 60°, 90°",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "angle_60",
            name: "Angle 60°",
            type: "values",
            description: "The given angle",
            value: "60°",
            importance: 10,
            x: 0, y: 60, z: 35
          },
          {
            id: "cosine_60_result",
            name: "Cosine 60° Result",
            type: "values",
            description: "The cosine of 60 degrees",
            value: "cos(60°) = 1/2 = 0.5",
            importance: 9,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "trigonometry", to: "cosine_function", type: "includes", strength: 9 },
          { from: "trigonometry", to: "special_angles", type: "includes", strength: 8 },
          { from: "angle_60", to: "cosine_60_result", type: "used to calculate", strength: 9 },
          { from: "special_angles", to: "cosine_60_result", type: "provides value for", strength: 8 }
        ]
      }
    },
    {
      id: 'trig_fund_03',
      problem: "Find tan(45°)",
      problemType: "trigonometry",
      difficulty: "fundamental",
      description: "Basic tangent function with common angle",
      graphData: {
        nodes: [
          {
            id: "trigonometry",
            name: "Trigonometry",
            type: "concepts",
            description: "Study of relationships between angles and sides of triangles",
            value: "Angle and side relationships",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "tangent_function",
            name: "Tangent Function",
            type: "concepts",
            description: "Ratio of opposite side to adjacent side",
            value: "tan(θ) = opposite/adjacent",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "special_angles",
            name: "Special Angles",
            type: "concepts",
            description: "Common angles with known trigonometric values",
            value: "30°, 45°, 60°, 90°",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "angle_45",
            name: "Angle 45°",
            type: "values",
            description: "The given angle",
            value: "45°",
            importance: 10,
            x: 0, y: 60, z: 35
          },
          {
            id: "tangent_45_result",
            name: "Tangent 45° Result",
            type: "values",
            description: "The tangent of 45 degrees",
            value: "tan(45°) = 1",
            importance: 9,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "trigonometry", to: "tangent_function", type: "includes", strength: 9 },
          { from: "trigonometry", to: "special_angles", type: "includes", strength: 8 },
          { from: "angle_45", to: "tangent_45_result", type: "used to calculate", strength: 9 },
          { from: "special_angles", to: "tangent_45_result", type: "provides value for", strength: 8 }
        ]
      }
    },
    {
      id: 'trig_fund_04',
      problem: "Find sin(90°)",
      problemType: "trigonometry",
      difficulty: "fundamental",
      description: "Basic sine function with right angle",
      graphData: {
        nodes: [
          {
            id: "trigonometry",
            name: "Trigonometry",
            type: "concepts",
            description: "Study of relationships between angles and sides of triangles",
            value: "Angle and side relationships",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "sine_function",
            name: "Sine Function",
            type: "concepts",
            description: "Ratio of opposite side to hypotenuse",
            value: "sin(θ) = opposite/hypotenuse",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "special_angles",
            name: "Special Angles",
            type: "concepts",
            description: "Common angles with known trigonometric values",
            value: "30°, 45°, 60°, 90°",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "angle_90",
            name: "Angle 90°",
            type: "values",
            description: "The given angle",
            value: "90°",
            importance: 10,
            x: 0, y: 60, z: 35
          },
          {
            id: "sine_90_result",
            name: "Sine 90° Result",
            type: "values",
            description: "The sine of 90 degrees",
            value: "sin(90°) = 1",
            importance: 9,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "trigonometry", to: "sine_function", type: "includes", strength: 9 },
          { from: "trigonometry", to: "special_angles", type: "includes", strength: 8 },
          { from: "angle_90", to: "sine_90_result", type: "used to calculate", strength: 9 },
          { from: "special_angles", to: "sine_90_result", type: "provides value for", strength: 8 }
        ]
      }
    },
    {
      id: 'trig_fund_05',
      problem: "Find cos(0°)",
      problemType: "trigonometry",
      difficulty: "fundamental",
      description: "Basic cosine function with zero angle",
      graphData: {
        nodes: [
          {
            id: "trigonometry",
            name: "Trigonometry",
            type: "concepts",
            description: "Study of relationships between angles and sides of triangles",
            value: "Angle and side relationships",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "cosine_function",
            name: "Cosine Function",
            type: "concepts",
            description: "Ratio of adjacent side to hypotenuse",
            value: "cos(θ) = adjacent/hypotenuse",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "special_angles",
            name: "Special Angles",
            type: "concepts",
            description: "Common angles with known trigonometric values",
            value: "0°, 30°, 45°, 60°, 90°",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "angle_0",
            name: "Angle 0°",
            type: "values",
            description: "The given angle",
            value: "0°",
            importance: 10,
            x: 0, y: 60, z: 35
          },
          {
            id: "cosine_0_result",
            name: "Cosine 0° Result",
            type: "values",
            description: "The cosine of 0 degrees",
            value: "cos(0°) = 1",
            importance: 9,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "trigonometry", to: "cosine_function", type: "includes", strength: 9 },
          { from: "trigonometry", to: "special_angles", type: "includes", strength: 8 },
          { from: "angle_0", to: "cosine_0_result", type: "used to calculate", strength: 9 },
          { from: "special_angles", to: "cosine_0_result", type: "provides value for", strength: 8 }
        ]
      }
    }
  ],
  
  intermediate: [
    {
      id: 'trig_inter_01',
      problem: "Find sin(120°)",
      problemType: "trigonometry",
      difficulty: "intermediate",
      description: "Sine function with angle in second quadrant",
      graphData: {
        nodes: [
          {
            id: "trigonometry",
            name: "Trigonometry",
            type: "concepts",
            description: "Study of relationships between angles and sides of triangles",
            value: "Angle and side relationships",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "sine_function",
            name: "Sine Function",
            type: "concepts",
            description: "Ratio of opposite side to hypotenuse",
            value: "sin(θ) = opposite/hypotenuse",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "reference_angles",
            name: "Reference Angles",
            type: "concepts",
            description: "Angles used to find values in other quadrants",
            value: "180° - θ for second quadrant",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "angle_120",
            name: "Angle 120°",
            type: "values",
            description: "The given angle",
            value: "120°",
            importance: 10,
            x: 0, y: 60, z: 35
          },
          {
            id: "reference_angle_60",
            name: "Reference Angle 60°",
            type: "values",
            description: "Reference angle for 120°",
            value: "180° - 120° = 60°",
            importance: 8,
            x: -120, y: 150, z: -15
          },
          {
            id: "sine_120_result",
            name: "Sine 120° Result",
            type: "values",
            description: "The sine of 120 degrees",
            value: "sin(120°) = sin(60°) = √3/2",
            importance: 9,
            x: 120, y: 150, z: -15
          }
        ],
        links: [
          { from: "trigonometry", to: "sine_function", type: "includes", strength: 9 },
          { from: "trigonometry", to: "reference_angles", type: "includes", strength: 8 },
          { from: "angle_120", to: "reference_angle_60", type: "used to find", strength: 8 },
          { from: "reference_angle_60", to: "sine_120_result", type: "used to calculate", strength: 9 }
        ]
      }
    },
    {
      id: 'trig_inter_02',
      problem: "Find cos(135°)",
      problemType: "trigonometry",
      difficulty: "intermediate",
      description: "Cosine function with angle in second quadrant",
      graphData: {
        nodes: [
          {
            id: "trigonometry",
            name: "Trigonometry",
            type: "concepts",
            description: "Study of relationships between angles and sides of triangles",
            value: "Angle and side relationships",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "cosine_function",
            name: "Cosine Function",
            type: "concepts",
            description: "Ratio of adjacent side to hypotenuse",
            value: "cos(θ) = adjacent/hypotenuse",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "reference_angles",
            name: "Reference Angles",
            type: "concepts",
            description: "Angles used to find values in other quadrants",
            value: "180° - θ for second quadrant",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "angle_135",
            name: "Angle 135°",
            type: "values",
            description: "The given angle",
            value: "135°",
            importance: 10,
            x: 0, y: 60, z: 35
          },
          {
            id: "reference_angle_45",
            name: "Reference Angle 45°",
            type: "values",
            description: "Reference angle for 135°",
            value: "180° - 135° = 45°",
            importance: 8,
            x: -120, y: 150, z: -15
          },
          {
            id: "cosine_135_result",
            name: "Cosine 135° Result",
            type: "values",
            description: "The cosine of 135 degrees",
            value: "cos(135°) = -cos(45°) = -√2/2",
            importance: 9,
            x: 120, y: 150, z: -15
          }
        ],
        links: [
          { from: "trigonometry", to: "cosine_function", type: "includes", strength: 9 },
          { from: "trigonometry", to: "reference_angles", type: "includes", strength: 8 },
          { from: "angle_135", to: "reference_angle_45", type: "used to find", strength: 8 },
          { from: "reference_angle_45", to: "cosine_135_result", type: "used to calculate", strength: 9 }
        ]
      }
    },
    {
      id: 'trig_inter_03',
      problem: "Find tan(150°)",
      problemType: "trigonometry",
      difficulty: "intermediate",
      description: "Tangent function with angle in second quadrant",
      graphData: {
        nodes: [
          {
            id: "trigonometry",
            name: "Trigonometry",
            type: "concepts",
            description: "Study of relationships between angles and sides of triangles",
            value: "Angle and side relationships",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "tangent_function",
            name: "Tangent Function",
            type: "concepts",
            description: "Ratio of opposite side to adjacent side",
            value: "tan(θ) = opposite/adjacent",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "reference_angles",
            name: "Reference Angles",
            type: "concepts",
            description: "Angles used to find values in other quadrants",
            value: "180° - θ for second quadrant",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "angle_150",
            name: "Angle 150°",
            type: "values",
            description: "The given angle",
            value: "150°",
            importance: 10,
            x: 0, y: 60, z: 35
          },
          {
            id: "reference_angle_30",
            name: "Reference Angle 30°",
            type: "values",
            description: "Reference angle for 150°",
            value: "180° - 150° = 30°",
            importance: 8,
            x: -120, y: 150, z: -15
          },
          {
            id: "tangent_150_result",
            name: "Tangent 150° Result",
            type: "values",
            description: "The tangent of 150 degrees",
            value: "tan(150°) = -tan(30°) = -1/√3",
            importance: 9,
            x: 120, y: 150, z: -15
          }
        ],
        links: [
          { from: "trigonometry", to: "tangent_function", type: "includes", strength: 9 },
          { from: "trigonometry", to: "reference_angles", type: "includes", strength: 8 },
          { from: "angle_150", to: "reference_angle_30", type: "used to find", strength: 8 },
          { from: "reference_angle_30", to: "tangent_150_result", type: "used to calculate", strength: 9 }
        ]
      }
    },
    {
      id: 'trig_inter_04',
      problem: "Find sin(240°)",
      problemType: "trigonometry",
      difficulty: "intermediate",
      description: "Sine function with angle in third quadrant",
      graphData: {
        nodes: [
          {
            id: "trigonometry",
            name: "Trigonometry",
            type: "concepts",
            description: "Study of relationships between angles and sides of triangles",
            value: "Angle and side relationships",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "sine_function",
            name: "Sine Function",
            type: "concepts",
            description: "Ratio of opposite side to hypotenuse",
            value: "sin(θ) = opposite/hypotenuse",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "reference_angles",
            name: "Reference Angles",
            type: "concepts",
            description: "Angles used to find values in other quadrants",
            value: "θ - 180° for third quadrant",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "angle_240",
            name: "Angle 240°",
            type: "values",
            description: "The given angle",
            value: "240°",
            importance: 10,
            x: 0, y: 60, z: 35
          },
          {
            id: "reference_angle_60",
            name: "Reference Angle 60°",
            type: "values",
            description: "Reference angle for 240°",
            value: "240° - 180° = 60°",
            importance: 8,
            x: -120, y: 150, z: -15
          },
          {
            id: "sine_240_result",
            name: "Sine 240° Result",
            type: "values",
            description: "The sine of 240 degrees",
            value: "sin(240°) = -sin(60°) = -√3/2",
            importance: 9,
            x: 120, y: 150, z: -15
          }
        ],
        links: [
          { from: "trigonometry", to: "sine_function", type: "includes", strength: 9 },
          { from: "trigonometry", to: "reference_angles", type: "includes", strength: 8 },
          { from: "angle_240", to: "reference_angle_60", type: "used to find", strength: 8 },
          { from: "reference_angle_60", to: "sine_240_result", type: "used to calculate", strength: 9 }
        ]
      }
    },
    {
      id: 'trig_inter_05',
      problem: "Find cos(300°)",
      problemType: "trigonometry",
      difficulty: "intermediate",
      description: "Cosine function with angle in fourth quadrant",
      graphData: {
        nodes: [
          {
            id: "trigonometry",
            name: "Trigonometry",
            type: "concepts",
            description: "Study of relationships between angles and sides of triangles",
            value: "Angle and side relationships",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "cosine_function",
            name: "Cosine Function",
            type: "concepts",
            description: "Ratio of adjacent side to hypotenuse",
            value: "cos(θ) = adjacent/hypotenuse",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "reference_angles",
            name: "Reference Angles",
            type: "concepts",
            description: "Angles used to find values in other quadrants",
            value: "360° - θ for fourth quadrant",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "angle_300",
            name: "Angle 300°",
            type: "values",
            description: "The given angle",
            value: "300°",
            importance: 10,
            x: 0, y: 60, z: 35
          },
          {
            id: "reference_angle_60",
            name: "Reference Angle 60°",
            type: "values",
            description: "Reference angle for 300°",
            value: "360° - 300° = 60°",
            importance: 8,
            x: -120, y: 150, z: -15
          },
          {
            id: "cosine_300_result",
            name: "Cosine 300° Result",
            type: "values",
            description: "The cosine of 300 degrees",
            value: "cos(300°) = cos(60°) = 1/2",
            importance: 9,
            x: 120, y: 150, z: -15
          }
        ],
        links: [
          { from: "trigonometry", to: "cosine_function", type: "includes", strength: 9 },
          { from: "trigonometry", to: "reference_angles", type: "includes", strength: 8 },
          { from: "angle_300", to: "reference_angle_60", type: "used to find", strength: 8 },
          { from: "reference_angle_60", to: "cosine_300_result", type: "used to calculate", strength: 9 }
        ]
      }
    }
  ]
}; 